import React, { useEffect, useState } from 'react';
import OperationDropdown from '../../../OperationDropdown';
import UserSelect from '../../../UserSelect';
import { UserDetails } from '../../../../types/user';
import { Button, CircularProgress, Stack, Tooltip } from '@mui/material';
import { DeleteForever } from '@mui/icons-material';
import SubmissionAlert from '../../../SubmissionAlert';
import AlertDialog from '../../../AlertDialog';
import { assignOperation, callAPISync, deleteOperation, deleteOperationData } from '../../../../utils/api';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { addNewRole, hasViewAllPermission, removeRole } from '../../../../store/user/userSlice';
import { removeOpSuffix, switchOpSuffix } from '../../../../utils/operations';
import { setUserRoles } from '../../../../store/tab/tabSlice';
import { getUserSystemRoles } from '../../../../utils/user';
import { useLogto } from '@logto/react';
import { addUserRoles, removeUserRoles } from '../../../../store/tab/tabSlice';

const ManageOperations: React.FC = () => {
	const dispatch = useAppDispatch();
	const logto = useLogto();

	const currentUser = useAppSelector((state) => state.user);
	const hasViewAll = useAppSelector((state) => hasViewAllPermission(state));
	const hasDelete = useAppSelector((state) => state.user.orgs).some((org) => org.permissions.includes('delete:operation'));

	const [selectedOperation, setSelectedOperation] = useState<string>('');

	const [selectedUploadUsers, setSelectedUploadUsers] = useState<Record<string, UserDetails>>({});
	const [selectedViewUsers, setSelectedViewUsers] = useState<Record<string, UserDetails>>({});

	const [submitting, setSubmitting] = useState<boolean>(false);
	const [submissionSuccess, setSubmissionSuccess] = useState<boolean>(true);
	const [submissionMessage, setSubmissionMessage] = useState<string[]>([]);

	const [showNoUserConfirm, setShowNoUserConfirm] = useState<boolean>(false);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);

	const modifyOperation = (opToModify: string, selectedUsers: Record<string, UserDetails>, displayOp: string) => {
		callAPISync(logto, (token) => {
			assignOperation(token, opToModify, Object.keys(selectedUsers))
				.then(async (res) => {
					if (res.ok) {
						const userResponse = (await res.json()) as {
							removedUsers: string[];
							assignedUsers: string[];
						};
						if (userResponse.assignedUsers.includes(currentUser.id)) {
							dispatch(addNewRole(opToModify));
							const roles = getUserSystemRoles(currentUser.orgs, [opToModify]);
							dispatch(addUserRoles(roles));
						} else if (userResponse.removedUsers.includes(currentUser.id)) {
							dispatch(removeRole(opToModify));
							const newUserRoles = [...currentUser.roles].filter((role) => role !== opToModify);
							const roles = getUserSystemRoles(currentUser.orgs, newUserRoles);
							dispatch(removeUserRoles(roles));
						}

						setSubmissionSuccess(true);
						setSubmissionMessage([
							`Successfully updated operation "${displayOp}"`,
							`${userResponse.removedUsers.length} user(s) removed`,
							`${userResponse.assignedUsers.length} user(s) assigned`,
						]);
						setSelectedUploadUsers({});
						setSelectedViewUsers({});
						setSelectedOperation('');
						setSubmitting(false);
					} else {
						setSubmissionSuccess(false);
						setSubmissionMessage([
							`Encountered an error when attempting to modify "${displayOp}"`,
							`${res.status}: ${res.statusText}`,
							`Try again or contact an administrator if this persists`,
						]);
						setSubmitting(false);
					}
				})
				.catch((e) => {
					setSubmissionSuccess(false);
					setSubmissionMessage([
						`Encountered an error when attempting to modify "${displayOp}"`,
						`${String(e)}`,
						`Try again or contact an administrator if this persists`,
					]);
					setSubmitting(false);
				});
		});
	};

	const onModify = () => {
		setSubmitting(true);

		if (selectedOperation.endsWith('-upload')) {
			modifyOperation(selectedOperation, selectedUploadUsers, removeOpSuffix(selectedOperation));
			modifyOperation(switchOpSuffix(selectedOperation), selectedViewUsers, removeOpSuffix(selectedOperation));
		} else {
			// legacy operations
			modifyOperation(selectedOperation, selectedUploadUsers, selectedOperation);
		}

		// refresh roles and therefore allowed tabs
		const roles = getUserSystemRoles(currentUser.orgs, currentUser.roles);
		dispatch(setUserRoles({ roles: roles }));
	};

	const onDelete = () => {
		if (selectedOperation.endsWith('-upload')) {
			handleDeleteOperation();
		} else {
			handleDeleteLegacyOperation();
		}
	};

	const handleDeleteOperation = () => {
		const opName = removeOpSuffix(selectedOperation);
		callAPISync(logto, (token) => {
			//ensue the operation data is deleted _before_ the operation
			deleteOperationData(token, opName)
				.then(() => {
					return Promise.all([deleteOperation(token, selectedOperation), deleteOperation(token, switchOpSuffix(selectedOperation))]);
				})
				.then((res) => {
					if (res.every((r) => r.ok)) {
						dispatch(removeRole(selectedOperation));
						dispatch(removeRole(switchOpSuffix(selectedOperation)));
						setSubmissionSuccess(true);
						setSubmissionMessage([`Successfully deleted operation "${opName}"`]);
						setSelectedOperation('');
						setSelectedUploadUsers({});
						setSelectedViewUsers({});
						setSubmitting(false);
					} else if (res[0].ok && res[1].ok) {
						dispatch(removeRole(selectedOperation));
						dispatch(removeRole(switchOpSuffix(selectedOperation)));
						setSubmissionSuccess(false);
						setSubmissionMessage([
							`Successfully deleted references to operation "${opName}"`,
							`However, an error occurred when deleting operation data: ${res[0].statusText}`,
							'You will no longer be able to access this operation, please contact an administrator if data requires deletion',
						]);
						setSelectedOperation('');
						setSelectedUploadUsers({});
						setSelectedViewUsers({});
						setSubmitting(false);
					} else {
						const errArray: string[] = [];
						if (!res[0].ok) {
							errArray.push(`Deleting operation data: ${res[0].statusText}`);
						}
						if (!res[0].ok || !res[1].ok) {
							let roleErr = 'Deleting operation role:';
							if (!res[0].ok) {
								roleErr += ` ${res[0].statusText}`;
							}
							if (!res[1].ok) {
								roleErr += ` ${res[1].statusText}`;
							}
							errArray.push(roleErr);
						}

						setSubmissionSuccess(false);
						setSubmissionMessage([
							`Encountered an error when attempting to delete "${opName}"`,
							...errArray,
							`Try again or contact an administrator if this persists`,
						]);
						setSubmitting(false);
					}
				});
		});
	};

	const handleDeleteLegacyOperation = () => {
		callAPISync(logto, (token) => {
			deleteOperationData(token, selectedOperation)
				.then((res) => {
					if (res.ok) {
						deleteOperation(token, selectedOperation)
							.then((res) => {
								if (res.ok) {
									dispatch(removeRole(selectedOperation));
									setSubmissionSuccess(true);
									setSubmissionMessage([`Successfully deleted operation "${selectedOperation}"`]);
									setSelectedOperation('');
									setSelectedUploadUsers({});
									setSelectedViewUsers({});
									setSubmitting(false);
								} else {
									setSubmissionSuccess(false);
									setSubmissionMessage([
										`Encountered an error when attempting to delete "${selectedOperation}"`,
										`${res.status}: ${res.statusText}`,
										`Try again or contact an administrator if this persists`,
									]);
									setSubmitting(false);
								}
							})
							.catch((e) => {
								setSubmissionSuccess(false);
								setSubmissionMessage([
									`Encountered an error when attempting to delete "${selectedOperation}"`,
									`${String(e)}`,
									`Try again or contact an administrator if this persists`,
								]);
								setSubmitting(false);
							});
					} else {
						setSubmissionSuccess(false);
						setSubmissionMessage([
							`Encountered an error when attempting to delete operation data for "${selectedOperation}"`,
							`${res.status}: ${res.statusText}`,
							`Try again or contact an administrator if this persists`,
						]);
						setSubmitting(false);
					}
				})
				.catch((e) => {
					setSubmissionSuccess(false);
					setSubmissionMessage([
						`Encountered an error when attempting to delete operation data for "${selectedOperation}"`,
						`${String(e)}`,
						`Try again or contact an administrator if this persists`,
					]);
					setSubmitting(false);
				});
		});
	};

	useEffect(() => {
		if (submissionMessage && Object.values(selectedUploadUsers).length === 0 && Object.values(selectedViewUsers).length === 0) {
			window.scrollTo(0, 0);
		}
	}, [submissionMessage]);

	return (
		<Stack
			sx={{
				width: '100%',
			}}
			spacing={2}
			data-testid="operations-tab-content"
			flex={1}
		>
			<SubmissionAlert
				showCondition={submissionMessage.length > 0}
				success={submissionSuccess}
				onClose={() => setSubmissionMessage([])}
				messages={submissionMessage}
			/>

			<Stack direction="row" data-testid="manage-operation-content">
				<OperationDropdown
					selectedOption={selectedOperation}
					setSelectedOption={setSelectedOperation}
					operationType="MANAGE"
					topTextOverride="Choose an operation to modify or assign user(s) to"
					loadAll={hasViewAll}
				/>

				<Tooltip title={selectedOperation && `Delete ${removeOpSuffix(selectedOperation)} Permanently`}>
					<span>
						<Button
							disabled={!selectedOperation || submitting || !hasDelete}
							variant="contained"
							color="error"
							sx={{ height: '56px', marginX: 1 }}
							onClick={() => {
								setSubmitting(true);
								setShowDeleteConfirm(true);
							}}
							aria-label={selectedOperation ? `Delete ${removeOpSuffix(selectedOperation)} Permanently` : 'Delete Operation Permanently'}
							data-testid="delete-operation-button"
						>
							{submitting ? <CircularProgress sx={{ mr: 1 }} /> : <DeleteForever sx={{ mr: 1 }} />}
							{submitting ? 'Deleting...' : 'Delete'}
						</Button>
					</span>
				</Tooltip>
			</Stack>

			<UserSelect
				key={selectedOperation}
				currentOperation={selectedOperation}
				operationType="MANAGE"
				selectedUploadUsers={selectedUploadUsers}
				selectedViewUsers={selectedViewUsers}
				setSelectedUploadUsers={setSelectedUploadUsers}
				setSelectedViewUsers={setSelectedViewUsers}
			/>

			<Button
				variant="contained"
				disabled={!selectedOperation || submitting}
				onClick={() => onModify()}
				size="large"
				data-testid="update-operation-button"
			>
				{submitting ? <CircularProgress color="inherit" /> : 'Update Operation'}
			</Button>

			<AlertDialog
				title="No users assigned to operation"
				message={[
					`No users have been assigned to operation ${selectedOperation}`,
					'You will be automatically assigned as the only user',
					'Continue?',
				]}
				isOpen={showNoUserConfirm}
				rightButtonMessage={'Cancel'}
				rightButtonAction={() => {
					setSubmitting(false);
					setShowNoUserConfirm(false);
				}}
				leftButtonMessage={'Confirm'}
				leftButtonAction={() => {
					setShowNoUserConfirm(false);
					onModify();
				}}
			/>

			<AlertDialog
				title="Delete?"
				message={[
					`Are you sure you want to delete ${removeOpSuffix(selectedOperation)}?`,
					'Any uploaded data will be lost, and you will need to recreate this operation if you would like to upload to it in future',
				]}
				isOpen={showDeleteConfirm}
				rightButtonMessage={'Cancel'}
				rightButtonAction={() => {
					setShowDeleteConfirm(false);
					setSubmitting(false);
				}}
				leftButtonMessage={'Confirm'}
				leftButtonAction={() => {
					setShowDeleteConfirm(false);
					onDelete();
				}}
			/>
		</Stack>
	);
};

export default ManageOperations;
